import { observer } from "mobx-react";
import React, { useState } from "react";
import {
  Stack,
  Button,
  Text,
  TextInput,
  Loader,
  SegmentedControl,
  Alert,
  Badge,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconArrowsUpDown,
  IconTrash,
} from "@tabler/icons-react";
import { Cubby } from "../../stores/Cubby";
import { MODALS } from "../../helpers/constants";

interface EditNostrConnectionForSendAndReceiveProps {
  cubby: Cubby;
}

const EditNostrConnectionForSendAndReceive = observer(
  ({ cubby }: EditNostrConnectionForSendAndReceiveProps) => {
    const connection = cubby.selectedNostrConnection;

    if (!connection) {
      return null;
    }

    const [isLoading, setIsLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [showRemoveWarning, setShowRemoveWarning] = useState(false);

    // Track name change
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      connection.setName(e.currentTarget.value);
      setHasChanges(true);
    };

    // Add default budget if none is set
    const handleAddBudget = () => {
      connection.setBudget(100000, 30, 1096);
      handleSaveConnection();
      setHasChanges(true);
    };

    // Clear the budget data and save the connection
    const handleConfirmRemoveBudget = () => {
      handleRemoveBudget();
      handleSaveConnection();
      setShowRemoveWarning(false);
    };

    // Clear the budget data
    const handleRemoveBudget = () => {
      connection.setBudget(null, null, null);
      setHasChanges(true);
    };

    // Track changes in budget amount
    const handleBudgetAmountChange = (value: string) => {
      connection.setBudget(
        Number(value),
        connection.budget_renewal_period_days,
        connection.connection_expiration_days
      );
      setHasChanges(true);
    };

    // Track changes in renewal period
    const handleRenewalPeriodChange = (value: string) => {
      connection.setBudget(
        connection.budget_amount_sats,
        Number(value),
        connection.connection_expiration_days
      );
      setHasChanges(true);
    };

    // Track changes in expiration
    const handleExpirationChange = (value: string) => {
      connection.setBudget(
        connection.budget_amount_sats,
        connection.budget_renewal_period_days,
        Number(value)
      );
      setHasChanges(true);
    };

    // Handle save connection
    const handleSaveConnection = () => {
      cubby.cubbyFastInfo("modify_nwc_connection", {
        nostrConnectionUuid: connection.uuid,
        name: connection.name,
        budgetAmountSats: connection.budget_amount_sats,
        budgetRenewalPeriodDays: connection.budget_renewal_period_days,
        connectionExpirationDays: connection.connection_expiration_days,
      });

      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        setHasChanges(false);
        cubby.setModal(MODALS.CONNECT_APPS);
        cubby.selectNostrConnection(undefined);
      }, 3000);
    };

    return (
      <Stack gap="md">
        <Badge
          color="teal"
          size="lg"
          leftSection={<IconArrowsUpDown size={16} />}
        >
          Send And Receive
        </Badge>

        <TextInput
          label="Name"
          value={connection.name}
          onChange={handleNameChange}
          disabled={isLoading}
        />

        {connection.budget_amount_sats === null ? (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Budget Recommended"
            color="red"
          >
            An app could quickly spend all your sats. Set a budget for
            protection.
          </Alert>
        ) : null}

        {connection.budget_amount_sats === null ? (
          <Button onClick={handleAddBudget} disabled={isLoading}>
            Add Budget
          </Button>
        ) : (
          <>
            <Text fw={500}>Spend no more than...</Text>
            <SegmentedControl
              value={String(connection.budget_amount_sats || "100000")}
              onChange={handleBudgetAmountChange}
              data={[
                { label: "10k sats", value: "10000" },
                { label: "100k sats", value: "100000" },
                { label: "1M sats", value: "1000000" },
                { label: "50M sats", value: "50000000" },
              ]}
              disabled={isLoading}
            />
            <Text fw={500}>Every...</Text>
            <SegmentedControl
              value={String(connection.budget_renewal_period_days || "30")}
              onChange={handleRenewalPeriodChange}
              data={[
                { label: "30 days", value: "30" },
                { label: "1 year", value: "365" },
              ]}
              disabled={isLoading}
            />
            <Text fw={500}>Expire connection in...</Text>
            <SegmentedControl
              value={String(connection.connection_expiration_days || "1095")}
              onChange={handleExpirationChange}
              data={[
                { label: "90 days", value: "90" },
                { label: "1 year", value: "365" },
                { label: "3 years", value: "1096" },
              ]}
              disabled={isLoading}
            />
            <Button
              color="red"
              variant="outline"
              leftSection={<IconTrash size={16} />}
              onClick={() => setShowRemoveWarning(true)}
              disabled={isLoading}
            >
              Remove Budget & Expiration
            </Button>
          </>
        )}

        {showRemoveWarning && (
          <Stack gap="sm">
            <Alert
              icon={<IconAlertTriangle size={16} />}
              title="Warning"
              color="red"
              withCloseButton
              onClose={() => setShowRemoveWarning(false)}
            >
              Removing the budget is potentially dangerous and could allow an
              app to drain your outbound funds.
            </Alert>
            <Button
              color="red"
              variant="filled"
              leftSection={<IconTrash size={16} />}
              onClick={handleConfirmRemoveBudget}
              mt="sm"
            >
              Remove Budget
            </Button>
          </Stack>
        )}

        {isLoading ? (
          <Loader />
        ) : (
          <Button onClick={handleSaveConnection} disabled={!hasChanges}>
            Save Connection
          </Button>
        )}
      </Stack>
    );
  }
);

export default EditNostrConnectionForSendAndReceive;
